import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import Dropdown from "@/components/ui/dropdown";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import {
  reportsExtendedApi,
  type InternPlacementResponse,
  type InternPlacementItem,
  type InternPlacementParams,
} from "../../services/reports-extended.api";
import { Employees, Approve, Document } from "@/components/icons";
import styles from "./InternProfileReportPage.module.scss";

const PAGE_SIZE = 20;

const buildStatusLabel = (t: (k: string) => string): Record<number, string> => ({
  0: t("internManagement.addModal.statusOptions.active"),
  1: t("internManagement.addModal.statusOptions.completed"),
});

const buildTypeLabel = (t: (k: string) => string): Record<number, string> => ({
  0: t("internManagement.addModal.internTypeOptions.university"),
  1: t("internManagement.highSchool"),
});

const buildDocLabel = (t: (k: string) => string): Record<number, string> => ({
  0: t("internProfile.docPending"),
  1: t("internProfile.approvedStatus"),
  2: t("internProfile.docRejected"),
});

const buildSubCategoryOptions = (t: (k: string) => string) => [
  { label: t("internProfile.all"), value: "" },
  { label: t("internManagement.addModal.internTypeOptions.mandatorySummer"), value: "0" },
  { label: t("internManagement.addModal.internTypeOptions.workplaceTraining"), value: "1" },
  { label: t("internManagement.addModal.internTypeOptions.skillTraining"), value: "2" },
];

const buildLocationOptions = (t: (k: string) => string) => [
  { label: t("internProfile.all"), value: "" },
  { label: t("internManagement.addModal.departmentTypeOptions.central"), value: "1" },
  { label: t("internManagement.addModal.departmentTypeOptions.provincial"), value: "2" },
];

const buildApplicationSourceOptions = (t: (k: string) => string) => [
  { label: t("internProfile.all"), value: "" },
  { label: t("internManagement.addModal.applicationSourceOptions.careerGate"), value: "0" },
  { label: t("internManagement.addModal.applicationSourceOptions.inPerson"), value: "1" },
];

interface InternReportExtraFilters {
  subCategory?: string;
  internshipLocation?: string;
  applicationSource?: string;
}

const InternProfileReportPage: React.FC = () => {
  const { t } = useTranslation();
  const STATUS_LABEL = useMemo(() => buildStatusLabel(t), [t]);
  const TYPE_LABEL = useMemo(() => buildTypeLabel(t), [t]);
  const DOC_LABEL = useMemo(() => buildDocLabel(t), [t]);
  const SUB_CATEGORY_OPTIONS = useMemo(() => buildSubCategoryOptions(t), [t]);
  const LOCATION_OPTIONS = useMemo(() => buildLocationOptions(t), [t]);
  const APPLICATION_SOURCE_OPTIONS = useMemo(() => buildApplicationSourceOptions(t), [t]);
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [extraFilter, setExtraFilter] = useState<InternReportExtraFilters>({});
  const [page, setPage] = useState(1);

  const params: InternPlacementParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
      subCategory:
        extraFilter.subCategory !== undefined && extraFilter.subCategory !== ""
          ? Number(extraFilter.subCategory)
          : undefined,
      internshipLocation:
        extraFilter.internshipLocation !== undefined &&
        extraFilter.internshipLocation !== ""
          ? Number(extraFilter.internshipLocation)
          : undefined,
      applicationSource:
        extraFilter.applicationSource !== undefined &&
        extraFilter.applicationSource !== ""
          ? Number(extraFilter.applicationSource)
          : undefined,
    }),
    [filter, extraFilter, page],
  );

  const { data, isLoading, error } = useReportData<
    InternPlacementResponse,
    InternPlacementParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getInternPlacement(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const byDepartment = data?.byDepartment ?? [];
  const byType = data?.byType ?? [];
  const byStatus = data?.byStatus ?? [];
  const bySchool = data?.bySchool ?? [];
  const bySubCategory = data?.bySubCategory ?? [];
  const byInternshipLocation = data?.byInternshipLocation ?? [];
  const byApplicationSource = data?.byApplicationSource ?? [];

  const columns: TableColumn<InternPlacementItem>[] = [
    { header: "AD SOYAD", accessor: (row) => row.fullName },
    {
      header: t("internProfile.headerSchoolDepartment"),
      accessor: (row) => (
        <div className={styles.schoolCell}>
          <span>{row.school}</span>
          <span className={styles.subInfo}>{row.schoolDepartment}</span>
        </div>
      ),
    },
    { header: "SINIF", accessor: (row) => row.grade },
    { header: t("internProfile.headerDepartment"), accessor: (row) => row.department ?? "—" },
    {
      header: t("internProfile.headerType"),
      accessor: (row) => TYPE_LABEL[row.internshipType] ?? "—",
    },
    {
      header: t("internProfile.headerStart"),
      accessor: (row) =>
        new Date(row.startDate).toLocaleDateString("tr-TR"),
    },
    {
      header: t("internProfile.headerEnd"),
      accessor: (row) => new Date(row.endDate).toLocaleDateString("tr-TR"),
    },
    {
      header: "BELGE",
      accessor: (row) => (
        <span
          className={`${styles.docBadge} ${
            row.documentStatus === 1
              ? styles.approved
              : row.documentStatus === 2
                ? styles.rejected
                : styles.pending
          }`}
        >
          {DOC_LABEL[row.documentStatus] ?? "—"}
        </span>
      ),
    },
    {
      header: "DURUM",
      accessor: (row) => (
        <span
          className={`${styles.statusBadge} ${row.status === 0 ? styles.active : styles.completed}`}
        >
          {STATUS_LABEL[row.status] ?? "—"}
        </span>
      ),
    },
  ];

  return (
    <ReportDetailLayout
      title={t("internProfile.title")}
      categoryLabel={t("reportCatalog.categories.operational.title")}
      description={t("internProfile.description")}
      exportSlot={
        <ReportExportButtons
          reportType="INTERN_PLACEMENT"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="stajyer-profili"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          extras={
            <>
              <div style={{ minWidth: 180 }}>
                <Dropdown
                  label="Alt Kategori"
                  placeholder={t("internProfile.all")}
                  options={SUB_CATEGORY_OPTIONS}
                  value={extraFilter.subCategory ?? ""}
                  selectedLabel={
                    SUB_CATEGORY_OPTIONS.find(
                      (o) => o.value === (extraFilter.subCategory ?? ""),
                    )?.label ?? ""
                  }
                  onSelect={(v) => {
                    const val = Array.isArray(v) ? v[0] : v;
                    setExtraFilter((p) => ({
                      ...p,
                      subCategory: val || undefined,
                    }));
                    setPage(1);
                  }}
                  allowClear
                />
              </div>
              <div style={{ minWidth: 160 }}>
                <Dropdown
                  label="Staj Yeri"
                  placeholder={t("internProfile.all")}
                  options={LOCATION_OPTIONS}
                  value={extraFilter.internshipLocation ?? ""}
                  selectedLabel={
                    LOCATION_OPTIONS.find(
                      (o) =>
                        o.value === (extraFilter.internshipLocation ?? ""),
                    )?.label ?? ""
                  }
                  onSelect={(v) => {
                    const val = Array.isArray(v) ? v[0] : v;
                    setExtraFilter((p) => ({
                      ...p,
                      internshipLocation: val || undefined,
                    }));
                    setPage(1);
                  }}
                  allowClear
                />
              </div>
              <div style={{ minWidth: 180 }}>
                <Dropdown
                  label={t("internProfile.applicationSourceLabel")}
                  placeholder={t("internProfile.all")}
                  options={APPLICATION_SOURCE_OPTIONS}
                  value={extraFilter.applicationSource ?? ""}
                  selectedLabel={
                    APPLICATION_SOURCE_OPTIONS.find(
                      (o) =>
                        o.value === (extraFilter.applicationSource ?? ""),
                    )?.label ?? ""
                  }
                  onSelect={(v) => {
                    const val = Array.isArray(v) ? v[0] : v;
                    setExtraFilter((p) => ({
                      ...p,
                      applicationSource: val || undefined,
                    }));
                    setPage(1);
                  }}
                  allowClear
                />
              </div>
            </>
          }
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("internProfile.kpiTotal")}
          value={summary?.total ?? 0}
          icon={<Employees width={18} height={18} />}
        />
        <ReportKPICard
          title={t("internProfile.kpiActive")}
          value={summary?.active ?? 0}
          icon={<Employees width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title={t("internProfile.kpiCompleted")}
          value={summary?.completed ?? 0}
          icon={<Approve width={18} height={18} />}
          footnote={
            summary
              ? t("internProfile.kpiCompletionRate", {
                  rate: summary.completionRate.toFixed(1),
                })
              : undefined
          }
        />
        <ReportKPICard
          title={t("internProfile.kpiPendingDocs")}
          value={summary?.pendingDocs ?? 0}
          icon={<Document width={18} height={18} />}
          accent="warning"
        />
      </div>

      <div className={styles.chartRow}>
        <ReportChart
          title={t("internProfile.departmentDistribution")}
          type="bar"
          isLoading={isLoading}
          isEmpty={byDepartment.length === 0}
          series={[
            {
              name: "Stajyer",
              data: byDepartment.map((b) => b.count),
            },
          ]}
          options={{
            xaxis: { categories: byDepartment.map((b) => b.label) },
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            colors: ["#4f46e5"],
          }}
        />
        <ReportChart
          title="Staj Tipi"
          type="donut"
          isLoading={isLoading}
          isEmpty={byType.length === 0}
          series={byType.map((b) => b.count)}
          options={{
            labels: byType.map((b) => b.label),
            colors: ["#4f46e5", "#10b981"],
          }}
          height={280}
        />
      </div>

      <div className={styles.chartRow}>
        <ReportChart
          title={t("internProfile.statusDistribution")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byStatus.length === 0}
          series={byStatus.map((b) => b.count)}
          options={{
            labels: byStatus.map((b) => b.label),
            colors: ["#10b981", "#6366f1"],
          }}
          height={280}
        />
        <ReportChart
          title={t("internProfile.subCategoryDistribution")}
          type="donut"
          isLoading={isLoading}
          isEmpty={bySubCategory.length === 0}
          series={bySubCategory.map((b) => b.count)}
          options={{
            labels: bySubCategory.map((b) => b.label),
            colors: ["#f59e0b", "#4f46e5", "#10b981"],
          }}
          height={280}
        />
      </div>

      <div className={styles.chartRow}>
        <ReportChart
          title={t("internProfile.locationDistribution")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byInternshipLocation.length === 0}
          series={byInternshipLocation.map((b) => b.count)}
          options={{
            labels: byInternshipLocation.map((b) => b.label),
            colors: ["#6366f1", "#f59e0b"],
          }}
          height={280}
        />
        <ReportChart
          title={t("internProfile.applicationSourceDistribution")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byApplicationSource.length === 0}
          series={byApplicationSource.map((b) => b.count)}
          options={{
            labels: byApplicationSource.map((b) => b.label),
            colors: ["#4f46e5", "#10b981"],
          }}
          height={280}
        />
      </div>

      <div className={styles.chartRow}>
        <ReportChart
          title={t("internProfile.schoolDistribution")}
          type="bar"
          isLoading={isLoading}
          isEmpty={bySchool.length === 0}
          series={[
            {
              name: "Stajyer",
              data: bySchool.map((b) => b.count),
            },
          ]}
          options={{
            xaxis: { categories: bySchool.map((b) => b.label) },
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            colors: ["#10b981"],
          }}
        />
      </div>

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("internProfile.noData") : t("internProfile.noIntern")}
      />
    </ReportDetailLayout>
  );
};

export default InternProfileReportPage;
